/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { createDialogRoute, NumberTypes } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { DialogInput } from '@gh/core-ui';
import { buildNumberColumn, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { addMonths, firstDayOfMonth, lastDayOfMonth } from '@gh/core-util';
import {
  getWipSelectedDate,
  WipDetails,
  wipDetailsToJournal1,
  wipDetailsToJournal2,
  WipJournalLogShortItem,
} from '../../resources/wip.model';

export interface WipJournalsDialogConfig {
  details: WipDetails;
}

@Component({
  standalone: false,
  selector: 'gh-wip-journals-dialog',
  templateUrl: 'wip-journals-dialog.component.html',
  styleUrls: ['wip-journals-dialog.component.scss'],
})
export class WipJournalsDialogComponent implements OnInit, DialogInput<WipJournalsDialogConfig> {
  input: WipJournalsDialogConfig;

  journal1: WipJournalLogShortItem[];
  journal2: WipJournalLogShortItem[];

  lastDayOfSelectedMonth: Date;
  firstDayOfNextMonth: Date;

  journal1GridDefinition: GridDefinition;
  journal2GridDefinition: GridDefinition;

  constructor(private dialogRef: MatDialogRef<any>,
              private translations: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    this.journal1GridDefinition = this.buildGridDefinition();
    this.journal2GridDefinition = this.buildGridDefinition();

    const { details } = this.input;

    this.journal1 = wipDetailsToJournal1(details);
    this.journal2 = wipDetailsToJournal2(details);

    const selectedDate = getWipSelectedDate(details);
    if (selectedDate) {
      this.lastDayOfSelectedMonth = lastDayOfMonth(selectedDate);
      this.firstDayOfNextMonth = firstDayOfMonth(addMonths(selectedDate, 1));
    }
  }

  onPost(): void {
    this.dialogRef.close(true);
  }

  onBack(): void {
    this.dialogRef.close();
  }

  private buildGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual(
      [
        { field: 'code', headerName: this.translations.get('lbl_account_code'), width: 150 },
        { field: 'name', headerName: this.translations.get('lbl_payment_account_name'), width: 200 },
        {
          field: 'debit',
          headerName: this.translations.get('lbl_debit_dollar'),
          ...buildNumberColumn(NumberTypes.Price),
          width: 120,
        },
        {
          field: 'credit',
          headerName: this.translations.get('lbl_credit_dollar'),
          ...buildNumberColumn(NumberTypes.Price),
          width: 120,
        },
      ],
      {
        getRowId: (params) => String(params.data.code),
        defaultColDef: {
          sortable: false,
        },
      });
  }
}

export const wipJournalsDialogRoute = createDialogRoute({
  component: WipJournalsDialogComponent,
});
